import { AnimatePresence, m } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { confirmedDateOptionId } from "../api/attendance";
import { useAddDateOptions, useDateOptionsForEvent } from "../api/date-options";
import { VoteAnswer, useGetEvent, useGetEventParticipants } from "../api/events-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import { useAddVotes, useResetVotesForMember } from "../api/vote-api";
import { useVoteChangeAuditsForOption } from "../api/vote-audit-api";
import AvatarCircle from "../components/Avatar";
import { AttendanceToggle } from "../components/dss/AttendanceToggle";
import { Icon } from "../components/dss/Icon";
import { LiveDot } from "../components/dss/LiveDot";
import { EASE, FadeIn, useEnterAnimation } from "../components/dss/Motion";
import { Eyebrow } from "../components/dss/Primitives";
import { Skeleton } from "../components/dss/Skeleton";
import {
  AttendanceMeter,
  AttendanceTally,
  Roster,
  RosterEntry,
  Tally,
} from "../components/events/AttendanceRoster";
import { EventHero } from "../components/events/EventHero";
import Toast from "../components/Toast";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { LogeMember } from "../types/entities";

/**
 * One block of the page: a bracketed mono caption, optionally with a figure
 * set against it on the right, over its content.
 */
const Section = ({
  label,
  meta,
  children,
}: {
  label: string;
  meta?: string;
  children: React.ReactNode;
}) => (
  <div data-section={label}>
    <div className="flex items-baseline justify-between gap-3 mb-2.5">
      <Eyebrow>[ {label} ]</Eyebrow>
      {meta && (
        <span className="font-mono text-[9px] tracking-[1.2px] uppercase text-ink3">{meta}</span>
      )}
    </div>
    {children}
  </div>
);

const EventDetails = () => {
  const { eventId } = useParams();
  const { data: event, isLoading } = useGetEvent(eventId);
  const { data: members = [] } = useLogeMembers();
  const { data: participants = [] } = useGetEventParticipants(Number(eventId));
  const { data: dateOptions = [] } = useDateOptionsForEvent(eventId);
  const { member, hasRole } = useSupabaseAuth();

  // Get the editable date option ID to fetch audit records for it
  const editableOptionId = confirmedDateOptionId(dateOptions, event?.date);
  const { data: auditRecords = [] } = useVoteChangeAuditsForOption(editableOptionId);
  const navigate = useNavigate();
  const animateIn = useEnterAnimation(isLoading || !event);
  const { mutateAsync: addVotes } = useAddVotes();
  const { mutateAsync: resetVotes } = useResetVotesForMember();
  const { mutateAsync: addDateOptions } = useAddDateOptions();
  // Answers the page is showing ahead of the server agreeing to them, keyed
  // by member. The switch has to move under the finger — a pill that waits
  // out a round-trip before sliding reads as a control that didn't register
  // the tap — so a tap paints its answer here first and the write catches up.
  const [optimistic, setOptimistic] = useState<Record<number, VoteAnswer>>({});
  // The most recent answer asked for per member, which isn't always the one a
  // finishing write is carrying: tap twice and the first write comes back
  // stale. Only the last one is allowed to hand the display back to the
  // server.
  const latestIntent = useRef<Record<number, VoteAnswer>>({});
  // Each write is a soft-delete of the old rows followed by an insert, so two
  // overlapping ones can interleave into two live votes for the same date —
  // which the partial unique index then rejects. Chaining keeps a rapid
  // change of mind sequential rather than making it an error.
  const writes = useRef<Promise<void>>(Promise.resolve());
  // Who the admin's attendance toggle currently targets — null until they
  // pick someone from the roster below it.
  const [selectedMemberId, setSelectedMemberId] = useState<LogeMember["id"] | null>(null);
  // Whether the admin tools at the foot of the page — editing anyone's
  // attendance, the link to the full edit form — are open. Collapsed by
  // default: they're the one part of this page a plain member never sees at
  // all, and an admin reading a samling like everyone else shouldn't have
  // to look past a roster-editing control to get to the roster.
  const [adminOpen, setAdminOpen] = useState(false);
  // A failed attendance edit (most likely RLS rejecting the write) used to
  // fail completely silently — the dim flash reverted and nothing else told
  // the tapper it hadn't worked. Surface it instead of leaving that as a
  // guessing game.
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const host = members.find((m) => m.id === event?.host);

  const isAdmin = hasRole("ADMIN");
  const confirmed = event?.status === "confirmed";
  const cancelled = event?.status === "cancelled";
  // The host isn't asked to vote on their own samling, so they don't get the
  // ballot button either — they'd only be bounced to the results.
  const votingOpen =
    event?.show_vote_link && event?.status === "draft" && member?.id !== event?.host;

  // Each member's whole history on the confirmed date, newest first — the
  // roster needs the oldest entry as much as the newest, since what it flags
  // is whether an answer has ended up somewhere other than where it started,
  // and a round trip back to the original is not a change worth warning about.
  const auditTrailsByMemberId = new Map<number, typeof auditRecords>();
  for (const record of auditRecords) {
    const trail = auditTrailsByMemberId.get(record.user_id);
    if (trail) trail.push(record);
    else auditTrailsByMemberId.set(record.user_id, [record]);
  }
  // `??`, not `||`: VoteAnswer.NO is 0, and an optimistic afbud has to win
  // over the server's stale answer exactly like a ja or a måske does.
  const answerFor = (memberId: LogeMember["id"]): VoteAnswer | undefined =>
    optimistic[memberId] ??
    (participants.find((p) => p.user_id === memberId)?.answer as VoteAnswer | undefined);

  // The answer a member's chip/avatar should actually show — `answerFor`,
  // except that the host defaults to "attending" rather than "no answer"
  // when nothing else is on record for them: hosting a samling means
  // attending it, since nobody asks the host to vote on their own
  // gathering. An admin can still record a real answer for the host (via
  // the picker below), and once one exists it wins over this default
  // exactly like it would for anyone else — `??`, not `||`, so an explicit
  // afbud (VoteAnswer.NO is 0) isn't mistaken for "nothing recorded".
  const effectiveAnswerFor = (memberId: LogeMember["id"]): VoteAnswer | undefined =>
    answerFor(memberId) ?? (memberId === event?.host ? VoteAnswer.YES : undefined);

  // Stop showing our own guess for this member and let the server's answer
  // through again — either because it has caught up, or because the write
  // failed and the guess was wrong.
  const clearOptimistic = (memberId: LogeMember["id"]) =>
    setOptimistic((current) => {
      const rest = { ...current };
      delete rest[memberId];
      return rest;
    });

  // Whether the given member's attendance toggle can actually write: a plain
  // member can only edit once the confirmed date's date_option already
  // exists, since creating it is an admin-only write under RLS. Admin can
  // always start editing — ensureEditableOptionId (above) creates the
  // option lazily on their first change.
  const canEditAttendance = (memberId: LogeMember["id"]) =>
    confirmed && memberId !== event?.host && (isAdmin || !!editableOptionId);

  // A confirmed event only has a date_options row to vote against if it was
  // confirmed via the vote-and-close flow. One confirmed by hand-editing its
  // date on the legacy admin form (see EditEvent.tsx) never gets one — so
  // editableOptionId comes back undefined for plenty of real, already-
  // confirmed samlinger. Rather than leave attendance permanently
  // uneditable there, an admin's first tap creates the missing option
  // on the fly, matching the date the event was actually confirmed for.
  // Remembered rather than re-read from `dateOptions`, which only learns
  // about the new option once the refetch lands: without this, a second tap
  // arriving first would create a second option for the same date.
  const createdOptionId = useRef<number | undefined>(undefined);
  const ensureEditableOptionId = async (): Promise<number | undefined> => {
    if (editableOptionId) return editableOptionId;
    if (createdOptionId.current) return createdOptionId.current;
    if (!event?.date || !isAdmin) return undefined;
    const created = await addDateOptions({ eventId: event.id, selectedDates: [event.date] });
    createdOptionId.current = created?.[0]?.id;
    return createdOptionId.current;
  };

  // Sets (rather than cycles) a member's attendance from the 3-step toggle.
  // Always goes through reset-then-insert: a member can go straight from
  // "ja" to "måske" without ever passing through "ingen svar", and skipping
  // the reset would leave both rows on record instead of replacing one with
  // the other.
  const setMemberAttendance = (memberId: LogeMember["id"], next: VoteAnswer) => {
    if (!event) return;
    setOptimistic((current) => ({ ...current, [memberId]: next }));
    latestIntent.current[memberId] = next;

    writes.current = writes.current.then(async () => {
      try {
        const optionId = await ensureEditableOptionId();
        if (!optionId) {
          clearOptimistic(memberId);
          setToast({
            message: "Kunne ikke oprette afstemning for datoen — prøv igen",
            type: "error",
          });
          return;
        }
        await resetVotes({ eventId: event.id, memberId });
        await addVotes({ logeMemberId: memberId, selectedDates: { [optionId]: next } });
        // Only the two queries an attendance edit can actually change.
        // Invalidating everything also put the member roster and the event
        // list back in flight on every tap, which is a lot of refetching for
        // a page whose whole point is now that it holds still.
        //
        // Awaited so the guess stands until real data is back to replace it;
        // dropping it any earlier flashes the old answer through the switch.
        await queryClient.invalidateQueries({ queryKey: ["event_participants", event.id] });
        await queryClient.invalidateQueries({ queryKey: ["dateOptions", eventId] });
        // The write may have just recorded a late change against this option,
        // and the roster's alert reads it from its own query — without this
        // the sheet keeps showing whichever change was cached before the tap.
        await queryClient.invalidateQueries({ queryKey: ["voteAudits", optionId] });
        if (latestIntent.current[memberId] === next) clearOptimistic(memberId);
      } catch (error) {
        // The most likely cause is RLS rejecting the write (e.g. the
        // "members reset their own votes" migration not yet applied) — logged
        // in full since the toast can only carry a human-readable line.
        console.error("Failed to update attendance", error);
        clearOptimistic(memberId);
        setToast({ message: "Kunne ikke opdatere deltagelse", type: "error" });
      }
    });
  };

  // Everyone the samling actually asks to vote: the roster minus the host,
  // who is attending their own gathering by default and isn't balloted on
  // it. This is about the vote, not about who an admin can edit — see
  // `otherMembers` below, which the host is very much part of.
  const editableMembers = members.filter((m) => m.id !== event?.host);
  // Everyone the admin picker offers: everyone but the admin themselves, who
  // has their own switch directly above it — listing them here too would put
  // two controls for the same answer on one screen. The host is included:
  // "attending by default" is still an answer an admin can override, same as
  // any member's.
  const otherMembers = members.filter((m) => m.id !== member?.id);
  // How many the vote is actually asking — the loge minus the host, under
  // the name the ballot count reads it by. Not `otherMembers.length`: that
  // one now includes the host, who still isn't part of a date vote.
  const votersCount = editableMembers.length;
  const selectedMember = otherMembers.find((m) => m.id === selectedMemberId);
  const avatarTone = (answer: VoteAnswer | undefined) =>
    answer === VoteAnswer.YES
      ? "green"
      : answer === VoteAnswer.MAYBE
        ? "yellow"
        : answer === VoteAnswer.NO
          ? "red"
          : "slate";

  // While the date is still open, the useful question isn't who is coming —
  // nobody can commit to a date that doesn't exist yet — but who has been
  // heard from at all. Counted from the ballots themselves rather than the
  // participants view, which only ever reports on a settled date.
  const voterIds = new Set(
    dateOptions
      .flatMap((option) => option.votes.map((vote) => vote.user_id))
      .filter((id) => id !== event?.host),
  );

  const countOf = (answer: VoteAnswer) =>
    editableMembers.filter((m) => answerFor(m.id) === answer).length;
  // The host's own contribution follows their effective answer — attending
  // by default, or whatever an admin has set instead — rather than always
  // landing in "yes": once overridden, the host counts wherever their real
  // answer puts them, same as anyone else.
  const hostAnswer = host ? effectiveAnswerFor(host.id) : undefined;
  const tally: Tally = {
    yes: countOf(VoteAnswer.YES) + (hostAnswer === VoteAnswer.YES ? 1 : 0),
    maybe: countOf(VoteAnswer.MAYBE) + (hostAnswer === VoteAnswer.MAYBE ? 1 : 0),
    no: countOf(VoteAnswer.NO) + (hostAnswer === VoteAnswer.NO ? 1 : 0),
    none: editableMembers.filter((m) => answerFor(m.id) === undefined).length,
  };

  // The roster, in a fixed order that no answer ever changes: the host, then
  // the loge as the member list gives it. Grouping by answer would be a
  // tidier read, but every answer would then re-sort the page under the
  // finger that gave it — see Roster.
  const rosterEntries: RosterEntry[] = [
    ...(host ? [host] : []),
    ...editableMembers,
  ]
    .filter(
      (m) =>
        m.id === event?.host ||
        answerFor(m.id) !== undefined ||
        // Who specifically hasn't answered is for the people who can act on
        // it — an admin chasing replies, or the member who is themselves the
        // one missing — not a roll call of absent members for everyone
        // browsing the samling. It also means a row can never appear or
        // disappear as a result of the reader's own tap: their own is always
        // listed, and an admin sees the whole loge whatever anyone answers.
        isAdmin ||
        m.id === member?.id,
    )
    .map((m) => ({
      member: m,
      answer: effectiveAnswerFor(m.id),
      isHost: m.id === event?.host,
      isViewer: m.id === member?.id,
      auditTrail: auditTrailsByMemberId.get(m.id),
    }));

  return (
    // No pt-safe here: the hero (or, while loading, its skeleton) is the
    // very first thing on the page and paints its own way up under the
    // notch, rather than leaving a strip of paper above it.
    <div className="mx-auto max-w-md bg-paper min-h-app">
      {isLoading || !event ? (
        <div className="pt-safe pb-8">
          {/* Still on paper here — the skeleton doesn't know its own colour
              yet — so the back link keeps its original muted styling and its
              own top gutter, matching how the loaded hero insets itself. */}
          <div className="px-7 pt-2 pb-4">
            <button
              onClick={() => navigate("/")}
              className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
            >
              <Icon name="chevron-left" size={12} />
              <span>Kalender</span>
            </button>
          </div>
          <Skeleton className="h-[300px] rounded-b-[22px]" />
          <div className="px-7 pt-7 flex flex-col gap-7">
            <div>
              <Skeleton className="w-28 h-3 mb-2.5" />
              <Skeleton className="w-full h-12 rounded-lg" />
            </div>
            <div>
              <Skeleton className="w-24 h-3 mb-2.5" />
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[72px] rounded-lg" />
                ))}
              </div>
              <Skeleton className="w-full h-[156px] rounded-lg mt-3" />
            </div>
          </div>
        </div>
      ) : (
        <FadeIn enabled={animateIn}>
          <EventHero
            event={event}
            host={host}
            votingOpen={!!votingOpen}
            onBack={() => navigate("/")}
          />

          <div className="px-7 pt-7 flex flex-col gap-7 pb-10">
            {/* Everything about the vote, while there still is one: how far
                it has got, and the ballot itself. Directly under the hero —
                it is the only thing anyone can act on until a date is
                settled, and it shouldn't sit at the foot of a page of
                reading matter. */}
            {!confirmed && !cancelled && (
              <Section label="Afstemning" meta={votingOpen ? "Åben" : undefined}>
                {dateOptions.length > 0 ? (
                  <div className="bg-card border border-line-soft rounded-lg px-4 py-3.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-display text-[15px] font-bold uppercase tracking-tight">
                        {voterIds.size} af {votersCount} har stemt
                      </div>
                      <div className="font-mono text-[10px] tracking-[1.2px] text-ink3 tabular-nums">
                        {votersCount > 0 ? Math.round((voterIds.size / votersCount) * 100) : 0}%
                      </div>
                    </div>
                    <div className="mt-2.5 h-1.5 rounded-full bg-paper2 overflow-hidden">
                      <div
                        className="h-full bg-vote-yes transition-[width] duration-500 ease-out"
                        style={{
                          width: `${votersCount > 0 ? (voterIds.size / votersCount) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="font-mono text-[10px] tracking-[1.1px] text-ink3 uppercase">
                    Ingen datoer foreslået endnu
                  </div>
                )}

                {/* The one dead end this section used to be: no date options
                    and no way for the vært (or an admin) to do anything
                    about it short of knowing the edit URL by heart. Kept
                    reachable — as a smaller link rather than this same big
                    button — once dates exist too: it's also the only way
                    back to editing them or the vote's visibility once the
                    green CTA below has replaced this dead-end text, and once
                    show_vote_link is off there's nothing else here at all
                    pointing at that URL. */}
                {(isAdmin || member?.id === event.host) &&
                  (dateOptions.length === 0 ? (
                    <button
                      onClick={() => navigate(`/vote/${event.id}/edit`)}
                      className="mt-3 w-full py-3.5 px-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3"
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <Icon name="calendar-days" size={13} />
                        <span className="truncate">Opret afstemning</span>
                      </span>
                      <Icon name="arrow-right" size={13} />
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/vote/${event.id}/edit`)}
                      className="mt-3 font-mono text-[10px] tracking-wide uppercase text-bilberry font-semibold"
                    >
                      Redigér afstemning
                    </button>
                  ))}

                {votingOpen && (
                  <button
                    onClick={() => navigate(`/vote/${event.id}`)}
                    // Pine rather than ink: voting is the same action as on
                    // the calendar row this was opened from, and green means
                    // "vote" everywhere. Ink stays for the neutral admin
                    // action below.
                    className="mt-3 w-full py-3.5 px-4 rounded-lg bg-sage dss-cta-pine text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3"
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      {/* Same live cue as the calendar row this was opened from. */}
                      <LiveDot color="bg-vote-yes-soft" />
                      <span className="truncate">Stem på datoer</span>
                    </span>
                    <Icon name="arrow-right" size={13} />
                  </button>
                )}
              </Section>
            )}

            {/* Whatever the vært wrote about the samling, when there is
                anything — it belongs directly under the hero, above the
                machinery for answering. */}
            {event.description && (
              <Section label="Om samlingen">
                <div className="text-[13.5px] leading-relaxed text-ink2 whitespace-pre-line">
                  {event.description}
                </div>
              </Section>
            )}

            {/* Who is coming, once that is a question anyone can answer. A
                samling with no date yet has nothing to be counted on, so it
                gets this only if answers were somehow recorded anyway —
                a legacy event confirmed and then reopened, say. Never
                something the reader's own tap can switch on or off. */}
            {(confirmed || participants.length > 0) && (
              <Section
                label="Deltagelse"
                meta={confirmed ? undefined : "Foreløbig — datoen mangler"}
              >
                <AttendanceTally tally={tally} />
                <AttendanceMeter tally={tally} />
                <div className="mt-3.5">
                  <Roster entries={rosterEntries} />
                </div>
              </Section>
            )}

            {/* Controls for the viewer's own attendance. Everything above
                this point — the hero, the vote button — is fixed content,
                and everything in the two control blocks holds its height
                whatever the answer, so a tap can never move the switch it
                was aimed at. */}
            {confirmed && member && member.id !== event.host && (
              <Section label="Din deltagelse">
                {canEditAttendance(member.id) ? (
                  <>
                    <AttendanceToggle
                      value={answerFor(member.id)}
                      onChange={(next) => setMemberAttendance(member.id, next)}
                    />
                    {/* A fixed line under the switch rather than a note that
                        appears only while unanswered: the caption a first
                        answer would have removed used to take the whole page
                        up with it, on the one tap that matters most. */}
                    <div className="h-4 mt-2.5 font-mono text-[9.5px] tracking-[1.2px] text-ink3 uppercase">
                      {answerFor(member.id) === undefined
                        ? "Du har ikke svaret endnu"
                        : "Du kan ændre dit svar når som helst"}
                    </div>
                  </>
                ) : (
                  <div className="font-mono text-[10px] tracking-[1.1px] text-ink3 uppercase leading-relaxed">
                    Samlingen har ingen tilknyttet afstemning — deltagelse kan ikke redigeres endnu
                  </div>
                )}
              </Section>
            )}

            {/* Everything here is for an admin's own use managing the
                samling, never for reading it — so it sits last, collapsed,
                behind one toggle rather than as sections a plain member
                would never see mixed in among ones they do. */}
            {isAdmin && (
              <div>
                <button
                  onClick={() => setAdminOpen((open) => !open)}
                  aria-expanded={adminOpen}
                  className="w-full py-3.5 px-4 rounded-lg border border-line text-ink2 font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-between gap-2"
                >
                  <span className="flex items-center gap-2">
                    <Icon name="sliders" size={13} />
                    <span>Admin</span>
                  </span>
                  <Icon
                    name="chevron-right"
                    size={11}
                    className={`transition-transform duration-200 ${adminOpen ? "rotate-90" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {adminOpen && (
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-7 pt-7">
                        {/* Editing attendance is only meaningful once a date
                            is confirmed — same gate the member's own switch
                            above uses. */}
                        {confirmed && (
                          <Section label="Rediger deltagelse">
                            {/* Tinted by each member's current answer, so the
                                picker doubles as a compact roster — and so a
                                change shows up right beside the switch that
                                made it, rather than only in the list further
                                up the page. Includes the host: they attend by
                                default, but that default is still an answer
                                an admin can override, same as anyone else's. */}
                            {/* py-1, not just pb-1: `overflow-x-auto` here
                                also makes the vertical axis clip (a lone
                                non-visible axis forces the other to become
                                "auto" too), and the selected ring is a
                                box-shadow standing 2px proud of the circle
                                on every side — with no top padding to give
                                it room, that top arc was being cut flat by
                                the scroll container's own edge. */}
                            <div className="flex gap-3 overflow-x-auto -mx-1 px-1 py-1">
                              {otherMembers.map((m) => (
                                <AvatarCircle
                                  key={m.id}
                                  userId={m.id}
                                  size="sm"
                                  bgColor={avatarTone(effectiveAnswerFor(m.id))}
                                  selected={selectedMemberId === m.id}
                                  onClick={() => setSelectedMemberId(m.id)}
                                />
                              ))}
                            </div>
                            {/* Rendered whether or not anyone is picked, and
                                inert until one is. Mounting the switch on
                                selection would push anything below it down
                                the moment an admin tapped an avatar. */}
                            <div className="mt-3.5">
                              <div className="h-3 mb-2 font-mono text-[9.5px] tracking-[1.2px] text-ink3 uppercase">
                                {selectedMember
                                  ? // Named, because the picker scrolls: the
                                    // member being edited can be off-screen by
                                    // the time the switch is in reach, and a
                                    // ring around an avatar nobody can see is
                                    // not an answer to "whose is this?".
                                    `Ændrer svar for ${selectedMember.name}`
                                  : "Vælg et medlem ovenfor"}
                              </div>
                              {/* Keyed on the member so switching target
                                  remounts the control: without it the pill
                                  slides from the last member's answer to this
                                  one's, which reads as a change just made
                                  rather than a different person being looked
                                  at. */}
                              <AttendanceToggle
                                key={selectedMember?.id ?? "none"}
                                value={selectedMember ? effectiveAnswerFor(selectedMember.id) : undefined}
                                onChange={(next) =>
                                  selectedMember && setMemberAttendance(selectedMember.id, next)
                                }
                                disabled={!selectedMember}
                              />
                            </div>
                          </Section>
                        )}

                        <button
                          onClick={() => navigate(`/edit/${event.id}`)}
                          className="w-full py-3.5 rounded-lg border border-line text-ink2 font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                        >
                          <Icon name="sliders" size={13} />
                          <span>Redigér samling</span>
                        </button>
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </FadeIn>
      )}
      <Toast
        message={toast?.message ?? ""}
        type={toast?.type ?? "error"}
        visible={!!toast}
        onClose={() => setToast(null)}
      />
    </div>
  );
};

export default EventDetails;
